<template>
  <div class="setting-content--section">
    <BrandModal 
      v-if="selectedBrand"
      @close="setSelectedBrand(null)"
      @created="onCreatedCallback"
      @edited="onEditedCallback"
      @deleted="onDeleteCallback"
    />

    <ImportKvkModal
      v-if="showImportKvkModal"
      :target-data="targetData"
      @close-modal="showImportKvkModal = false"
    />

    <div v-if="!loading" class="brands-section">
      <div class="brands-section--header">
        <div class="brands-section--header__title">
          <h3>{{ $gettext('Brands and trade names') }}</h3>
          <p>{{ $gettext('Here you can add different trade names for different brands your company might use. You can select these different trade names on your invoice.') }}</p>
          <p class="text-warning" v-if="!currentCompany.is_premium">{{ $gettext('To manage and use different trade names, you have to be a Premium user.') }}</p>
        </div>
        <div>
          <GekkoButton
            @click="openBrandCreate()"
            :text="$gettext('Add trade name')"
            :icon="'fa-solid fa-plus'"
            class="ml-auto mb-3"
          />
        </div>
      </div>
      <div v-if="showMissingCompanyDataWarning" class="brands-wrap">
        <b-alert show class="gk-alert mb-0" variant="warning">
          <div @click.prevent="showImportKvkModal = true" v-html="missingDataWarningText"/>
        </b-alert>
      </div>
      <div v-else class="brands-wrap">
        <BrandCard
          v-if="!hasMainBrand || !availableBrands || availableBrands.length < 1"
          :brand="mainBrand"
          @click.native="openMainBrandModal"
        />
        <BrandCard
          v-for="(brand, index) in availableBrands" :key="'brand-' + index"
          :brand="brand"
          @click.native="openBrandEdit(brand)"
        />
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex"

import GekkoButton from "@/components/GekkoButton"

import BrandCard from "@/components/brands/BrandCard"
import BrandModal from "@/components/brands/BrandModal"
import BrandsOnboardingModal from "@/components/brands/BrandsOnboardingModal"

import ImportKvkModal from "@/components/dashboard/ui/ImportKvkModal"

import settingsPageSetup from "@/mixins/settingsPageSetup"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import ModalBase from "@/components/base_components/ModalBase"

export default {
  name: 'Brands',
  components: {
    ModalBase,
    GekkoButton,
    BrandCard,
    LoadingSpinner,
    BrandModal,
    ImportKvkModal,
    BrandsOnboardingModal
  },
  mixins: [ settingsPageSetup ],
  data () {
    return {
      pageSettings: {
        title: this.$gettext('Invoice design'),
        showCompanyPicker: true
      },
      showImportKvkModal: false,
      loading: true,
      savingImmediateBrand: false
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany',
      availableBrands: 'brands/getAvailableBrands',
      selectedBrand: 'brands/getSelectedBrand',
      mainBrand: 'brands/getMainBrand',
      hasMainBrand: "brands/hasMainBrand",
      companyMissingFields: 'main/getCompanyMissingFields',
      targetData: 'main/getCompanyRequiredData',
      brandById: "brands/getBrandById"
    }),
    showMissingCompanyDataWarning () {
      return (this.companyMissingFields.length > 0)
    },
    missingDataWarningText () {
      return this.$gettext('Your company profile is incomplete. <span class=\'missing-info-link\'>Click to edit</span>')
    }
  },
  created () {
    this.setupData()
  },
  methods: {
    ...mapMutations({
      setSelectedBrand: 'brands/SET_SELECTED_BRAND',
    }),
    ...mapActions({
      loadCompanyMainBrand: 'brands/loadCompanyMainBrand',
      loadBrandsMainData: 'brands/loadBrandsMainData',
      initNewBrand: 'brands/initNewBrand',
      copyToBrand: 'brands/copyToBrand'
    }),
    refreshCompanyData () {
      this.loading = true
      this.loadCompanyMainBrand(this.currentCompany.id).then(() => {
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    setupData () {
      this.loading = true
      this.loadBrandsMainData(this.currentCompany.id).then(() => {
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    openBrandEdit (brand) {
      if (this.currentCompany.is_premium) {
        this.setSelectedBrand(brand)
      } else {
        this.$store.commit('togglePremiumModal', 'web_brands')
      }
    },
    openBrandCreate () {
      if (this.currentCompany.is_premium) {
        this.initNewBrand()
      } else {
        this.$store.commit('togglePremiumModal', 'web_brands')
      }
    },
    onEditedCallback () {
      this.setupData()
      // this.setSelectedBrand(null)
    },
    onCreatedCallback () {
      this.setupData()
      this.setSelectedBrand(null)
    },
    onDeleteCallback () {
      this.setupData()
      this.setSelectedBrand(null)
    },
    openMainBrandModal () {
      if (!this.savingImmediateBrand) {
        if (this.showMissingCompanyDataWarning) {
          this.showImportKvkModal = true
        } else {
          // save a new brand immediately using company data
          this.savingImmediateBrand = true
          this.copyToBrand()
        }
      }
    }
  }
}
</script>
<style lang="scss">
  .brand-card-wrap {
    padding: 0.5rem;
    display: flex;
    width: 100%;
    cursor: pointer;

    .itsme {
      .connection-card__info--name { color: $colorBlue; }
    }
  }

</style>
<style scoped lang="scss">
  .brands-section {
    &:not(:last-child) {
      margin-bottom: 1rem;
    }
    &--header {
      &__title { max-width: 650px; }
      p { font-size: 0.875rem; opacity: 0.6; }
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      margin-bottom: 1rem;
      .btn-primary { width: 100%; }

      @media (min-width: 576px) {
        flex-direction: row;
        margin-bottom: 0;
        .btn-primary { width: auto; }
      }
    }
    .brands-wrap {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
      @media (min-width: '1200px') { 
        grid-template-columns: 1fr 1fr;
      }
    }
  }

  

</style>
